<template>
  <LabelAndHint
    v-slot="{ inputId, isSuccess, isWarning, isErr }"
    v-bind="{ ...props, error: props.error || invalid, errorMessages: props.errorMessages || errorMessages }"
    class="app-input"
  >
    <slot name="label" />

    <div
      class="input-wrapper"
      :class="{
        success: isSuccess,
        warning: isWarning,
        error: isErr,
        disabled: disabled,
        'hide-text': hideText,
      }"
      :data-color="color"
    >
      <input
        :id="inputId"
        ref="inputElement"
        :value="type === 'amount' ? formatAmount(props.modelValue ?? props.value) : (props.modelValue ?? props.value)"
        type="text"
        :inputmode="mode"
        :placeholder="placeholder"
        :disabled="disabled"
        :name="name"
        :maxlength="maxLength"
        :autocomplete="autocompleteOff ? 'some-text-to-block-chrome-autocomplete' : undefined"
        @input="onInput"
        @keydown="onKeydown"
        @focus="emit('focus')"
        @blur="onBlur"
      />
      <span v-if="suffix">
        {{ suffix }}
      </span>

      <slot name="append" />
    </div>
  </LabelAndHint>
</template>

<script setup lang="ts">
import { computed, ref, nextTick, watch } from 'vue'
import { formatAmount, formatDate } from '@staff/shared'
import LabelAndHint from '../AppTextField/LabelAndHint.vue'
import { useCursorPositioning, useInputValidation } from '../../composables'

const props = defineProps<{
  id?: string | number
  label?: string
  labelAsSpan?: boolean
  hint?: string | string[]
  hideDetails?: boolean
  success?: boolean
  successMessages?: string | string[]
  warning?: boolean
  warningMessages?: string | string[]
  error?: boolean
  errorMessages?: string | string[]

  rules?: ({ id: string; rule: (value: unknown) => boolean | string | Promise<boolean | string> } | null)[]
  validationOptions?: {
    validateOnBlur?: boolean
    validateOnInput?: boolean
    validateOnModelChange?: boolean
  }
  modelValue?: string | number | null
  value?: string | number | null
  type?: 'text' | 'amount' | 'date' | 'email' | 'tel'
  inputmode?: 'numeric' | 'email' | 'tel' | 'text' | 'decimal' | 'search' | 'url'
  placeholder?: string
  suffix?: string
  disabled?: boolean
  hideText?: boolean
  color?: 'white'
  name?: string
  maxLength?: string
  autocompleteOff?: boolean
}>()

const mode = computed(() => {
  if (props.inputmode) {
    return props.inputmode
  }

  switch (props.type) {
    case 'amount': {
      return 'numeric'
    }
    case 'email': {
      return 'email'
    }
    case 'tel': {
      return 'tel'
    }
    default: {
      return 'text'
    }
  }
})

const inputElement = ref<HTMLInputElement>()

if (props.type === 'amount') {
  useCursorPositioning(inputElement)
}

const { invalid, errorMessages, validate } = useInputValidation(
  () => props.modelValue ?? props.value,
  () => props.rules,
  inputElement,
)

const emit = defineEmits<{
  (e: 'update:modelValue', value: string): void
  (e: 'input', value: string): void
  (e: 'focus'): void
  (e: 'blur'): void
  (e: 'keydown', value: KeyboardEvent): void
}>()

const onInput = (event: Event) => {
  if (!(event.target instanceof HTMLInputElement)) {
    return
  }
  switch (props.type) {
    case 'amount': {
      const value = event.target.value === '' ? event.target.value : formatAmount(event.target.value.replace(/\D/g, ''))
      emit('update:modelValue', value)
      emit('input', value)
      break
    }
    case 'date': {
      const value = formatDate(event.target.value, 'YYYY-MM-DD')
      emit('update:modelValue', value)
      emit('input', value)
      break
    }
    default: {
      emit('update:modelValue', event.target.value)
      emit('input', event.target.value)
      break
    }
  }

  if (invalid.value || props.validationOptions?.validateOnInput) {
    nextTick(() => validate())
  }
}

watch(
  () => props.modelValue ?? props.value,
  () => {
    if (invalid.value || props.validationOptions?.validateOnModelChange) {
      nextTick(() => validate())
    }
  },
)

const onBlur = () => {
  emit('blur')

  if (props.validationOptions?.validateOnBlur !== false) {
    nextTick(() => validate())
  }
}

const onKeydown = (event: KeyboardEvent) => {
  emit('keydown', event)

  if (event.key === 'Escape') {
    onEscape(event)
  }
}

const onEscape = (e: Event) => {
  if (e.target instanceof HTMLInputElement) {
    e.target.blur()
  }
}
</script>

<style lang="scss" scoped>
.app-input {
  margin-bottom: 8px;
}

.input-wrapper {
  display: flex;
  border: 1.5px solid var(--color-border-default);
  width: 100%;
  margin-bottom: 0px;
  word-break: keep-all;
  align-items: center;
  gap: 12px;
  border-radius: 6px;
  height: 40px;
  padding: 6px 12px;
  margin-top: 0;
  border-width: 1px;

  input {
    font: inherit;
  }

  &[data-color='white'] {
    background-color: white !important;
  }

  span {
    font-family: 'Gellix';
  }

  &.disabled {
    cursor: not-allowed;
    border-color: var(--color-textfield-disabled-border);

    input {
      &::placeholder {
        color: var(--color-fg-disabled);
      }
    }

    span {
      color: var(--color-fg-disabled);
    }
  }

  &.hide-text {
    input {
      color: transparent !important;
    }
  }

  &:focus-within {
    border-color: var(--color-textfield-focus-border);
  }

  &.focus {
    border-color: var(--color-textfield-focus-border);
  }

  &.success {
    border-color: var(--color-textfield-success-border);
  }

  &.warning {
    border-color: var(--color-textfield-warning-border);
  }

  &.error {
    border-color: var(--color-textfield-error-border);
    background-color: var(--color-textfield-error-background) !important;
  }
}

input {
  font-family: 'Gellix';
  font-size: 18px;
  font-weight: 300;
  width: 100%;
  text-overflow: ellipsis;
  border: none;

  &:focus {
    outline: none;
  }

  &::placeholder {
    overflow: visible;
  }
}
</style>
